// src/hooks/useFixedPopoverPosition.js
// Migrated verbatim from prototype/between-the-lines-v2.html lines 7198-7243.
//
// Returns: { triggerRef, tooltipRef, pos, ready, recompute }
// Generic fixed-position popover helper (no current consumer after the Slice 1b
// Model-A badge removal; retained for reuse by future popovers).
//
// The hook hides the tooltip until the first measurement so users never see a
// flash at (0,0). Uses position: fixed coords; consumers render via portal.

import { useState, useEffect, useRef } from 'react';

export const useFixedPopoverPosition = (open, fallbackWidth = 288) => {
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const [pos, setPos] = useState(null);
    const [ready, setReady] = useState(false);
    const recompute = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const r = trigger.getBoundingClientRect();
        const tip = tooltipRef.current;
        const tw = tip && tip.offsetWidth  ? tip.offsetWidth  : fallbackWidth;
        const th = tip && tip.offsetHeight ? tip.offsetHeight : 200;
        const vw = window.innerWidth, vh = window.innerHeight;
        const margin = 16, gap = 8;
        // Center horizontally under the trigger.
        let left = r.left + r.width / 2 - tw / 2;
        if (left + tw > vw - margin) left = vw - tw - margin;
        if (left < margin) left = margin;
        // Prefer below; flip above if it would overflow the viewport.
        let top = r.bottom + gap;
        if (top + th > vh - margin) {
            const above = r.top - th - gap;
            top = above >= margin ? above : Math.max(margin, vh - th - margin);
        }
        setPos({ left, top });
        setReady(true);
    };
    useEffect(() => {
        if (!open) { setReady(false); setPos(null); return; }
        // First-pass measure with whatever the tooltip currently is,
        // then re-measure on the next frame once the browser has laid it out.
        recompute();
        const id = requestAnimationFrame(() => recompute());
        const onScroll = () => recompute();
        const onResize = () => recompute();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(id);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return { triggerRef, tooltipRef, pos, ready, recompute };
};
